import { Link, Navigate } from "react-router-dom";
import { Footer } from "@/components/Footer";
import { useContent } from "@/context/SiteContentContext";
import { useAuth } from "@/context/AuthContext";
import { NowPlayingCard } from "@/components/NowPlayingCard";

const HERO_IMG_DEFAULT = "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1600&q=85";
const IMG_CAFE = "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=900&q=80";
const IMG_INTERIOR = "https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?w=900&q=80";
const IMG_FLOWERS = "https://images.unsplash.com/photo-1490750967868-88aa4486c946?w=900&q=80";
const IMG_PORTRAIT = "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=800&q=80";

const Landing = () => {
  const { user, loading } = useAuth();
  const heroLogo = useContent("landing.hero.logo", "/brand/le-salon-full.png");
  const heroImg = useContent("landing.hero.image", HERO_IMG_DEFAULT);
  const heroTagline = useContent(
    "landing.hero.tagline",
    "A peaceful retreat where beauty, creativity, meaningful conversation and genuine connection are celebrated."
  );
  const ctaJoin = useContent("landing.cta.join", "Step Inside");
  const ctaTagline = useContent("landing.cta.tagline", "Free for now · subscriber privileges soon");
  const editorQuote = useContent(
    "landing.editor.quote",
    "The French don't chase beauty. They make small, daily appointments with it."
  );
  // If a signed-in user opens the PWA (or navigates to /), take them
  // straight into the salon instead of the marketing page.
  // NOTE: hooks above must run every render (Rules of Hooks) — do the redirect
  // after all useContent() calls, not before.
  if (!loading && user) {
    return <Navigate to="/feed" replace />;
  }
  return (
    <div className="app-shell" data-testid="landing-page">
      {/* TOP NAV (landing-specific, simpler) */}
      <div className="container-belle" style={{ paddingTop: 36, paddingBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" data-testid="landing-logo">
          <img src="/brand/le-salon-wordmark.png" alt="Le Salon" style={{ height: 46, display: "block" }} />
        </Link>
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          <Link to="/login" data-testid="landing-signin"
            style={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 500 }}>Sign in</Link>
          <Link to="/register" data-testid="landing-join" className="btn-primary" style={{ textDecoration: "none" }}>
            Join the salon
          </Link>
        </div>
      </div>

      {/* HERO with full ornate logo */}
      <section style={{ paddingBottom: 120 }}>
        <div className="container-belle hero-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
          {/* Left: full logo + tagline + CTA */}
          <div className="fade-up" style={{ animationDelay: "0.05s", textAlign: "center" }}>
            <img
              loading="lazy" decoding="async" src={heroLogo}
              alt="Le Salon — A gathering place for women who love living beautifully"
              style={{ width: "100%", maxWidth: 520, margin: "0 auto", display: "block" }}
            />
            <p className="font-serif" style={{ fontSize: 22, fontStyle: "italic", lineHeight: 1.5, color: "var(--text-secondary)", maxWidth: 520, margin: "40px auto 36px" }}>
              {heroTagline}
            </p>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
              <Link to="/register" data-testid="hero-cta-join" className="btn-primary" style={{ textDecoration: "none" }}>
                {ctaJoin}
              </Link>
            </div>
            <div className="overline" style={{ marginTop: 32, opacity: 0.8 }}>
              {ctaTagline}
            </div>
          </div>

          {/* Right: a single hero image */}
          <div className="fade-up" style={{ animationDelay: "0.18s", position: "relative" }}>
            <img src={heroImg} alt="Parisian morning"
              style={{ width: "100%", aspectRatio: "4/5", objectFit: "cover", display: "block" }} />
            <div style={{
              position: "absolute", left: -24, bottom: -24,
              background: "var(--bg-alt)", border: "1px solid var(--border-default)",
              padding: "20px 26px", maxWidth: 280,
            }}>
              <div className="overline">A note from the editor</div>
              <p className="font-serif" style={{ fontStyle: "italic", fontSize: 18, lineHeight: 1.45, margin: 0, marginTop: 8 }}>
                &ldquo;{editorQuote}&rdquo;
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* NOW PLAYING — hero card featuring the latest / editor-featured broadcast */}
      <NowPlayingCard />

      <hr className="blush-hairline" style={{ maxWidth: 1280, margin: "0 auto" }} />

      {/* PILLARS */}
      <section style={{ paddingTop: 96, paddingBottom: 64 }}>
        <div className="container-belle">
          <div style={{ maxWidth: 720, marginBottom: 64 }}>
            <div className="overline" style={{ color: "var(--brand-accent)" }}>What you'll find within</div>
            <h2 className="editorial-h1" style={{ fontSize: "clamp(36px, 5vw, 60px)", marginTop: 16 }}>
              A community, a letters archive, and quiet conversations.
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 32 }}>
            <Pillar tag="Available now" n="01" title="The Salon"
              copy="Share photographs, video, and quiet observations from your beautiful life. Like, comment, and follow other femmes du monde."
              img={IMG_CAFE} />
            <Pillar tag="Available now" n="02" title="The Letters"
              copy="Long-form letters and confessions on art de vivre — yours and ours. Beautifully-written stories that live inside the community."
              img={IMG_INTERIOR} />
            <Pillar tag="Available now" n="03" title="Private letters"
              copy="Direct messages between members. Quiet conversations, away from the feed. The way old friends used to write."
              img={IMG_PORTRAIT} />
          </div>

          <div style={{ marginTop: 48, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 32 }}>
            <Pillar tag="Coming soon" n="04" title="Live salons"
              copy="Cook alongside a chef in Lyon. Walk a Parisian flea market with a stylist. Live broadcasts from our members and editors."
              img={IMG_FLOWERS} comingSoon />
            <div style={{ background: "var(--bg-alt)", padding: 40, display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div className="overline" style={{ color: "var(--brand-accent)" }}>An invitation</div>
              <p className="font-serif" style={{ fontSize: 26, fontStyle: "italic", lineHeight: 1.35, margin: "16px 0 24px" }}>
                "The community begins the moment you arrive."
              </p>
              <Link to="/register" data-testid="pillar-cta" className="btn-accent" style={{ textDecoration: "none", textAlign: "center" }}>
                Reserve your seat
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section style={{ background: "var(--bg-alt)", padding: "96px 0", marginTop: 80 }}>
        <div className="container-belle" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80, alignItems: "center" }}>
          <div>
            <div className="overline" style={{ color: "var(--brand-accent)" }}>From our family</div>
            <h2 className="editorial-h1" style={{ fontSize: "clamp(32px, 4.5vw, 52px)", marginTop: 16, marginBottom: 28 }}>
              For readers of <em>Belle Inspiration</em>, and viewers of <em>Joie de Vivre TV</em>.
            </h2>
            <p className="editorial-body" style={{ fontSize: 17, marginBottom: 28 }}>
              Le Salon is the gathering place we always hoped to build for you. A digital salon
              that extends the magazine and the network into something living, daily, and yours.
            </p>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <a href="https://www.belleinspiration.com" target="_blank" rel="noreferrer" className="link-underline"
                style={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 500 }}>
                Belle Inspiration →
              </a>
              <a href="https://www.joiedevivretv.com" target="_blank" rel="noreferrer" className="link-underline"
                style={{ fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 500 }}>
                Joie de Vivre TV →
              </a>
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <img loading="lazy" decoding="async" src={IMG_INTERIOR} alt="" style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover" }} />
            <img loading="lazy" decoding="async" src={IMG_FLOWERS} alt="" style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", marginTop: 40 }} />
          </div>
        </div>
      </section>

      <section style={{ padding: "120px 0" }}>
        <div className="container-belle" style={{ textAlign: "center", maxWidth: 720, margin: "0 auto" }}>
          <div className="overline" style={{ color: "var(--brand-accent)" }}>The invitation stands</div>
          <h2 className="editorial-h1" style={{ fontSize: "clamp(36px, 5.5vw, 68px)", marginTop: 20, marginBottom: 28 }}>
            Come live<br />beautifully with us.
          </h2>
          <p className="editorial-body" style={{ fontSize: 18, marginBottom: 36 }}>
            Free to join. Open to anyone who finds joy in the slow, the elegant, and the small French rituals that make a life feel like art.
          </p>
          <Link to="/register" data-testid="final-cta-join" className="btn-primary" style={{ textDecoration: "none", padding: "16px 40px", fontSize: 13 }}>
            Join the salon
          </Link>
        </div>
      </section>

      <Footer />
      <style>{`
        @media (max-width: 900px) {
          [data-testid="landing-page"] section .container-belle[style*="grid-template-columns"] {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .footer-grid { grid-template-columns: 1fr 1fr !important; gap: 32px !important; }
        }
      `}</style>
    </div>
  );
};

const Pillar = ({ n, title, copy, img, tag, comingSoon }) => (
  <div className="card-belle card-belle-hover" style={{ padding: 0 }}>
    <div style={{ aspectRatio: "4/3", overflow: "hidden" }} className="media-zoom">
      <img loading="lazy" decoding="async" src={img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    </div>
    <div style={{ padding: 28 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span className="overline">{n}</span>
        {comingSoon ? <span className="badge-coming">{tag}</span> : <span className="overline" style={{ color: "var(--brand-accent)" }}>{tag}</span>}
      </div>
      <h3 className="font-serif" style={{ fontSize: 26, marginTop: 12, marginBottom: 12, fontWeight: 500 }}>{title}</h3>
      <p className="editorial-body" style={{ fontSize: 15, margin: 0 }}>{copy}</p>
    </div>
  </div>
);

export default Landing;
